import type { OfferCampaignLookupResponse } from '../types/offer-campaigns';
import { formatOfferCampaignDate } from './OfferCampaignDateField';

export function EligibilityStep({
  lookup,
  generating,
  canEnroll,
  onGenerate,
  onBack,
  onCancel,
}: {
  lookup: OfferCampaignLookupResponse;
  generating: boolean;
  canEnroll: boolean;
  onGenerate: () => void;
  onBack: () => void;
  onCancel: () => void;
}) {
  return (
    <>
      <PatientEligibilityCard lookup={lookup} />
      <div className="offer-campaign-actions">
        <button className="btn btn-primary" type="button" onClick={onGenerate} disabled={!canEnroll || generating}>{generating ? 'Generating...' : 'Generate Offer Code'}</button>
        <button className="btn btn-outline" type="button" onClick={onBack}>Back</button>
        <button className="btn btn-outline" type="button" onClick={onCancel}>Cancel</button>
      </div>
    </>
  );
}

export function PatientEligibilityCard({ lookup }: { lookup: OfferCampaignLookupResponse }) {
  return (
    <div className="offer-campaign-patient-card offer-patient-card offer-discount-card">
      <div>
        <small>Patient</small>
        <strong>{lookup.patientName || '-'}</strong>
        <span>{lookup.mrn} | {lookup.centerName || '-'}</span>
      </div>
      <div>
        <small>Age / DOB</small>
        <strong>{lookup.ageYears ?? '-'}</strong>
        <span>{formatOfferCampaignDate(lookup.dateOfBirth)}</span>
      </div>
      <div>
        <small>Registered</small>
        <strong>{formatIstDate(lookup.registrationTimestampIst || lookup.registrationTimestamp)}</strong>
        <span>Patient registration date</span>
      </div>
      <div>
        <small>Booking Window</small>
        <strong>{bookingWindowLabel(lookup.bookingWindowKey)}</strong>
        <span>Rules applied to this enrollment</span>
      </div>
      <div>
        <small>Eligible Discount</small>
        <strong>{lookup.discountAmountFormatted || currency(lookup.discountAmount)}</strong>
        <span>{lookup.discountFormulaLabel || '-'}</span>
      </div>
      <div>
        <small>{lookup.unlimitedSlots ? 'Campaign Capacity' : 'Remaining Slots'}</small>
        <strong>{lookup.unlimitedSlots ? 'Unlimited' : lookup.slotsRemaining}</strong>
        <span>{lookup.unlimitedSlots ? 'No slot limit' : `of ${lookup.campaignLimit}`}</span>
      </div>
    </div>
  );
}

function currency(value: number | null | undefined) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value);
}

function bookingWindowLabel(value: string | null | undefined) {
  if (!value) return 'Campaign period';
  const match = value.match(/^WINDOW[_ -]?(\d+)$/i);
  return match ? `Booking window ${match[1]}` : value.replaceAll('_', ' ').toLowerCase();
}

function formatIstDate(value: string | null | undefined) {
  return formatOfferCampaignDate(value);
}
